import React from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';

import { AppForm, AppFormLabel, AppSelect, AppTextField } from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { fullWidthToHalfWidthNumber } from '@/utils/japaneseText';

import { INSURANCE_TYPE_KOUKI } from './constants';
import type { TPatientCertificationForm } from './schema';

interface OptionItem {
  value: number;
  label: string;
}

interface InsuranceInfoSectionProps {
  control: Control<TPatientCertificationForm>;
  watch: UseFormWatch<TPatientCertificationForm>;
  setValue: UseFormSetValue<TPatientCertificationForm>;
  trigger: UseFormTrigger<TPatientCertificationForm>;
  insuranceType: number;
  relationshipOptions: OptionItem[];
  sexOptions: OptionItem[];
  patientCostOptions: OptionItem[];
  onInsurerNumberChange: () => void;
  onRelationshipChange: (value: number) => void;
  onCostRateDropdownChange: (value: number) => void;
  isLoading: boolean;
}

/** Same logic as RA2 `calcAge` — accepts "YYYY/MM/DD" or "YYYY-MM-DD". */
const calcAge = (birthdate: string): number | undefined => {
  const pattern = /^[0-9]{4}(\/|-)[0-9]{2}(\/|-)[0-9]{2}$/;
  if (!pattern.test(birthdate)) return undefined;

  const birth = Number(birthdate.replace(/[/-]/g, ''));
  const today = new Date();
  const target = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();

  return Math.floor((target - birth) / 10000);
};

export const InsuranceInfoSection: React.FC<InsuranceInfoSectionProps> = ({
  control,
  watch,
  setValue,
  trigger,
  insuranceType,
  relationshipOptions,
  sexOptions,
  patientCostOptions,
  onInsurerNumberChange,
  onRelationshipChange,
  onCostRateDropdownChange,
  isLoading,
}) => {
  const isKouki = insuranceType === INSURANCE_TYPE_KOUKI;

  return (
    <>
      <Controller
        name="insurer_number"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item
            label={<AppFormLabel label="保険者番号" />}
            validateStatus={error ? 'error' : undefined}
            help={error?.message}
          >
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <AppTextField
                {...field}
                value={field.value ?? ''}
                status={error ? 'error' : undefined}
                maxLength={8}
                placeholder="00000000"
                onChange={e => {
                  // BD 6.1 — convert fullwidth digits to halfwidth realtime
                  const converted = fullWidthToHalfWidthNumber(e.target.value);
                  field.onChange(converted);
                }}
                onBlur={() => {
                  field.onBlur();
                  onInsurerNumberChange();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    field.onBlur();
                    onInsurerNumberChange();
                    trigger();
                  }
                }}
                disabled={isLoading}
                className="w-full sm:w-[197px]"
              />
              <span className="form-control-plaintext min-w-0 break-words">{watch('insurer_name')}</span>
            </div>
          </AppForm.Item>
        )}
      />

      <AppForm.Item label={<AppFormLabel label="受診者負担" />}>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start">
          <Controller
            name="futan_ritsu"
            control={control}
            render={({ field }) => <AppTextField {...field} className="w-full sm:w-[197px]" disabled />}
          />

          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <AppFormLabel label="負担割合変更" className="whitespace-nowrap" />
            <div className="w-full sm:w-[120px]">
              <Controller
                name="patient_cost_rate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppSelect
                    {...field}
                    options={patientCostOptions}
                    className="w-full"
                    popupMatchSelectWidth={false}
                    status={error ? 'error' : undefined}
                    placeholder=""
                    onChange={value => {
                      field.onChange(value);
                      onCostRateDropdownChange(value as number);
                    }}
                    disabled={isLoading}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-start">
            <AppFormLabel label="続柄" className="whitespace-nowrap h-10 flex items-center" />
            <Controller
              name="relationship_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppForm.Item
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  className="mb-0 w-full sm:w-[120px]"
                >
                  <AppSelect
                    {...field}
                    options={relationshipOptions}
                    className="w-full"
                    status={error ? 'error' : undefined}
                    onChange={value => {
                      field.onChange(value);
                      onRelationshipChange(value as number);
                    }}
                    disabled={isLoading}
                  />
                </AppForm.Item>
              )}
            />
          </div>

          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-start">
            <AppFormLabel label="性別" className="whitespace-nowrap h-10 flex items-center" />
            <Controller
              name="sex_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppForm.Item
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  className="mb-0 w-full sm:w-[100px]"
                >
                  <AppSelect
                    {...field}
                    options={sexOptions}
                    className="w-full"
                    status={error ? 'error' : undefined}
                    disabled={isLoading}
                  />
                </AppForm.Item>
              )}
            />
          </div>
        </div>
      </AppForm.Item>

      <Controller
        name="date_of_birth"
        control={control}
        render={({ field, fieldState: { error } }) => {
          const format = parseFormat(JA_JP.format);
          return (
            <AppForm.Item
              label={<AppFormLabel label="生年月日" />}
              validateStatus={error ? 'error' : undefined}
              help={error?.message}
            >
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <RA2NDatePicker
                  value={field.value ?? ''}
                  onChange={v => {
                    field.onChange(v);
                    const date = parseDate(v || null, format);
                    if (!Number.isNaN(date.getTime())) {
                      const seireki = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
                      const age = calcAge(seireki);
                      if (typeof age === 'number') setValue('age', age);
                    }
                  }}
                  placeholder="元号00年00月00日"
                  disabled={isLoading}
                  className="w-full sm:w-[197px]"
                />
                <Controller
                  name="age"
                  control={control}
                  render={({ field: ageField }) => (
                    <AppTextField {...ageField} disabled className="w-full sm:w-[60px]" />
                  )}
                />
                <span>歳</span>
              </div>
            </AppForm.Item>
          );
        }}
      />

      {isKouki ? (
        <Controller
          name="card_number2"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AppForm.Item
              label={<AppFormLabel label="被保険者番号" />}
              validateStatus={error ? 'error' : undefined}
              help={error?.message}
            >
              <AppTextField
                {...field}
                status={error ? 'error' : undefined}
                className="w-full sm:w-[197px]"
                disabled={isLoading}
              />
            </AppForm.Item>
          )}
        />
      ) : (
        <Controller
          name="card_mark"
          control={control}
          render={({ field, fieldState: { error: markError } }) => (
            <AppForm.Item
              label={<AppFormLabel label="記号" />}
              validateStatus={markError ? 'error' : undefined}
              help={markError?.message}
            >
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <AppTextField
                  {...field}
                  status={markError ? 'error' : undefined}
                  className="w-full sm:w-[197px]"
                  maxLength={255}
                  disabled={isLoading}
                  onChange={e => {
                    field.onChange(e);
                    // Clear cross-field error as soon as 記号 or 番号 has a value.
                    trigger(['card_mark', 'card_number']);
                  }}
                />
                <AppFormLabel label="番号" />
                <Controller
                  name="card_number"
                  control={control}
                  render={({ field: numberField, fieldState: { error: numberError } }) => (
                    <AppTextField
                      {...numberField}
                      status={numberError ? 'error' : undefined}
                      className="w-full sm:w-[197px]"
                      disabled={isLoading}
                      onChange={e => {
                        numberField.onChange(e);
                        trigger(['card_mark', 'card_number']);
                      }}
                    />
                  )}
                />
              </div>
            </AppForm.Item>
          )}
        />
      )}

      <Controller
        name="qualification_date"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item
            label={<AppFormLabel label="資格取得日" />}
            validateStatus={error ? 'error' : undefined}
            help={error?.message}
          >
            <RA2NDatePicker
              value={field.value ?? ''}
              onChange={field.onChange}
              className="w-full sm:w-[197px]"
              placeholder="元号00年00月00日"
              disabled={isLoading}
            />
          </AppForm.Item>
        )}
      />

      <Controller
        name="expiration_date"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item
            label={<AppFormLabel label="有効期限" />}
            validateStatus={error ? 'error' : undefined}
            help={error?.message}
          >
            <RA2NDatePicker
              value={field.value ?? ''}
              onChange={field.onChange}
              className="w-full sm:w-[197px]"
              placeholder="元号00年00月00日"
              disabled={isLoading}
            />
          </AppForm.Item>
        )}
      />
    </>
  );
};
